/* eslint-disable indent*/
import { SetStateAction, useContext, useEffect, useState } from 'react';
import 'react-h5-audio-player/lib/styles.css';
import { useTranslation } from 'react-i18next';

import { IconButton, Label, LABEL_TYPE, Spinner, ZeroState } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { OfflineContext } from '@/contexts';
import { EventContext } from '@/contexts/EventProvider';
import { showToast, ToastType } from '@/helpers/toast';
import {
  BlueCheckIcon,
  BlueDownloadIcon,
  DeleteIcon,
  DragIndicator,
  EditPenIcon,
  MusicIcon,
  MusicNoteIcon
} from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';
import { Asset, DownloadSong, TributeVideoAsset } from '@/types';

import { EditSongPopup } from './EditSongPopup';

type ChosenSongsContainerProps = {
  audioAssets: Array<TributeVideoAsset> | Array<Asset> | undefined;
  songUrl: string | undefined;
  setSongUrl: React.Dispatch<SetStateAction<string | undefined>>;
  deleteSong: (id: number, index: number) => void;
  isTab?: boolean;
  isUploaderPage?: boolean;
  uploadingSong?: DownloadSong;
  isUploading?: boolean;
  changePreviewData: (assetName: string) => void;
  setAudioAssets?: (assets: Asset[]) => void;
  hasEvent?: boolean;
  eventId?: number;
  playlistId?: number;
  onZeroStateIconClick?: () => void;
  readOnly?: boolean;
  hasOfflineMusicDownloadAccess?: boolean;
};

export const ChosenSongsContainer = ({
  audioAssets,
  songUrl,
  setSongUrl,
  deleteSong,
  isTab,
  isUploaderPage,
  uploadingSong,
  isUploading,
  changePreviewData,
  setAudioAssets,
  hasEvent = true,
  eventId,
  playlistId,
  onZeroStateIconClick,
  readOnly = false,
  hasOfflineMusicDownloadAccess = false
}: ChosenSongsContainerProps) => {
  const [t] = useTranslation('common');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [reorderedAssets, setReorderedAssets] = useState(audioAssets);
  const [showEditMusicPopup, setShowEditMusicPopup] = useState<boolean>(false);
  const [editingSongUrl, setEditingSongUrl] = useState<string | undefined>(undefined);
  const [editingAssetName, setEditingAssetName] = useState<string | undefined>(undefined);
  const [editingSongIndex, setEditingSongIndex] = useState<number | undefined>(undefined);
  const [isClearingOfflineDownloads, setIsClearingOfflineDownloads] = useState(false);

  const {
    downloadSong,
    downloadedSongIds,
    downloadedPlaylistIds,
    downloadProgress,
    getOfflineSongsForEvent,
    removeOfflinePlaylist,
    removeOfflineEventWithSongs
  } = useContext(OfflineContext);
  const { eventData } = useContext(EventContext);
  const hasOfflineEventDownloads = !!eventId && getOfflineSongsForEvent(eventId).length > 0;
  const hasOfflinePlaylistDownloads = !!playlistId && downloadedPlaylistIds.has(playlistId);
  const shouldShowClearOfflineDownloads =
    Boolean(isTab && (eventId || playlistId)) &&
    (hasOfflineEventDownloads || hasOfflinePlaylistDownloads);

  useEffect(() => {
    setReorderedAssets(audioAssets);
  }, [audioAssets]);

  const handleSongSaved = (updatedAsset: Asset) => {
    if (editingSongIndex === undefined) {
      return;
    }

    const nextAssets = [...(reorderedAssets || [])] as Array<Asset | TributeVideoAsset>;
    if (!nextAssets[editingSongIndex]) {
      return;
    }

    if (isTab || isUploaderPage) {
      nextAssets[editingSongIndex] = updatedAsset;
      setReorderedAssets(nextAssets as Asset[]);
      setAudioAssets?.(nextAssets as Asset[]);
    } else {
      const currentSong = nextAssets[editingSongIndex] as TributeVideoAsset;
      nextAssets[editingSongIndex] = {
        ...currentSong,
        asset: updatedAsset
      };
      setReorderedAssets(nextAssets as TributeVideoAsset[]);
      setAudioAssets?.(nextAssets as unknown as Asset[]);
    }
  };

  const songsToDisplay = hasEvent ? (reorderedAssets ?? audioAssets) : reorderedAssets;

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    const updatedAssets = [...(reorderedAssets ?? [])];
    const [draggedItem] = updatedAssets.splice(draggedIndex, 1);
    updatedAssets.splice(index, 0, draggedItem);

    setReorderedAssets(updatedAssets as Asset[]);
    if (audioAssets) {
      setAudioAssets?.(updatedAssets as Asset[]);
    }
    setDraggedIndex(null);
  };

  const handleDownloadSong = (asset: Asset) => {
    if (!eventId) {
      return;
    }

    void downloadSong(asset, {
      id: eventId,
      name: eventData?.name ?? '',
      description: eventData?.description,
      startDateAndTime: eventData?.startDateAndTime,
      organizationName: eventData?.organization?.name,
      status: eventData?.status
    });
  };

  const handleClearOfflineDownloads = async () => {
    if (isClearingOfflineDownloads) {
      return;
    }

    setIsClearingOfflineDownloads(true);
    try {
      if (playlistId) {
        await removeOfflinePlaylist(playlistId);
      } else if (eventId) {
        await removeOfflineEventWithSongs(eventId);
      }

      showToast(ToastType.SUCCESS, t('offlineDownloadsCleared'));
    } catch {
      showToast(ToastType.ERROR, t('offlineDownloadsClearError'));
    } finally {
      setIsClearingOfflineDownloads(false);
    }
  };

  return (
    <div
      className='mt-4 rounded-lg bg-accent-ghost-white px-4 pb-4 md:mt-0 md:p-4'
      data-testid={TestIds.eventMusic.chosenSongs}>
      <div className='flex items-center justify-between'>
        <Label
          text={t('chosenSongs')}
          type={LABEL_TYPE.H4}
          className='mb-2 pt-4 text-primary-blue md:mb-5 md:ml-2 md:mt-1 md:pt-0'
        />
        {shouldShowClearOfflineDownloads && (
          <div
            className={
              'mb-2 flex cursor-pointer flex-row items-center border-none bg-transparent pt-4 text-accent-red md:mb-5 md:mr-2 md:mt-1 md:pt-0'
            }
            onClick={() => {
              void handleClearOfflineDownloads();
            }}>
            {t('clearOfflineDownloads')}
          </div>
        )}
      </div>

      {songsToDisplay?.length === 0 && (!isTab || !isUploading) ? (
        <div className='m-auto w-1/2'>
          <ZeroState
            icon={
              <MusicNoteIcon fill='var(--color-secondary-steel-blue)' className='size-[30px]' />
            }
            titleFirstLine={t('addMusicTitleOne')}
            titleSecondLine={t('addMusicTitleTwo')}
            className='h-[290px]'
            onIconClick={onZeroStateIconClick}
          />
        </div>
      ) : (
        <ul
          className={`overflow-y-auto overflow-x-hidden scrollbar ${
            isTab ? 'max-h-[350px]' : 'max-h-48'
          }`}>
          {songsToDisplay?.map((song: Asset | TributeVideoAsset, index) => {
            const assetUrl =
              (song as Asset)?.assetUrl || (song as TributeVideoAsset)?.asset?.assetUrl;
            const assetName =
              (song as Asset)?.assetName || (song as TributeVideoAsset)?.asset?.assetName;
            return (
              <li
                className='flex items-center py-1'
                data-testid={getTestId(
                  TestIds.eventMusic.chosenSongItem,
                  isTab
                    ? String((song as Asset)?.id ?? assetName)
                    : String((song as TributeVideoAsset)?.asset?.id ?? assetName)
                )}
                key={
                  isTab ? (song as Asset)?.id : (song as TributeVideoAsset)?.asset?.id + '.' + index
                }
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}>
                {!hasEvent && !readOnly && <DragIndicator className='cursor-pointer' />}
                <MusicIcon fill='var(--color-primary-black)' className='h-4 w-6' />
                <p
                  className={`ml-3 max-w-[75%] cursor-pointer truncate whitespace-nowrap text-sm font-normal ${
                    assetUrl === songUrl && '!font-bold'
                  }`}
                  onClick={() => {
                    setSongUrl(
                      isTab || isUploaderPage
                        ? (song as Asset)?.assetUrl
                        : (song as TributeVideoAsset).asset?.assetUrl
                    );
                    changePreviewData?.(assetName);
                  }}>
                  {assetName}
                </p>
                {(song as Asset)?.duration && !hasEvent && (
                  <p
                    className={
                      'ml-auto cursor-pointer truncate whitespace-nowrap text-sm font-normal text-gray-400'
                    }>
                    {~~(((song as Asset)?.duration ?? 0) / 60)}:
                    {((song as Asset)?.duration ?? 0) % 60 < 10
                      ? `0${((song as Asset)?.duration ?? 0) % 60}`
                      : `${((song as Asset)?.duration ?? 0) % 60}`}
                  </p>
                )}
                <div className={`${hasEvent ? 'ml-auto' : 'ml-2'} flex gap-2`}>
                  {isTab &&
                    hasEvent &&
                    eventId &&
                    (() => {
                      const asset = song as Asset;
                      const isDownloaded = downloadedSongIds.has(asset.id);
                      const isDownloading = !!downloadProgress[asset.id];
                      if (isDownloaded) {
                        return (
                          <div className='flex size-6 items-center'>
                            <BlueCheckIcon className='size-4 text-primary-blue' />
                          </div>
                        );
                      }
                      if (!hasOfflineMusicDownloadAccess) {
                        return null;
                      }
                      return (
                        <IconButton
                          variant={IconButtonVariant.SECONDARY}
                          handleOnClick={() => handleDownloadSong(asset)}
                          disabled={isDownloading}
                          className={isDownloading ? 'opacity-40' : ''}>
                          <BlueDownloadIcon
                            className={`size-4 text-primary-blue ${
                              isDownloading ? 'animate-pulse' : ''
                            }`}
                            title={t('downloadForOffline')}
                          />
                        </IconButton>
                      );
                    })()}
                  {hasEvent && (
                    <IconButton
                      variant={IconButtonVariant.SECONDARY}
                      dataTestId={TestIds.eventMusic.chosenSongEditButton}
                      handleOnClick={() => {
                        const url =
                          isTab || isUploaderPage
                            ? (song as Asset)?.assetUrl
                            : (song as TributeVideoAsset)?.asset?.assetUrl;

                        const assetName =
                          isTab || isUploaderPage
                            ? (song as Asset)?.assetName
                            : (song as TributeVideoAsset)?.asset?.assetName;

                        setEditingSongUrl(url);
                        setEditingAssetName(assetName);
                        setEditingSongIndex(index);
                        setShowEditMusicPopup(true);
                      }}
                      className={'ml-auto'}>
                      <EditPenIcon className='text-primary-blue' />
                    </IconButton>
                  )}
                  {!readOnly && (
                    <IconButton
                      variant={IconButtonVariant.SECONDARY}
                      handleOnClick={() =>
                        deleteSong(
                          isTab || isUploaderPage
                            ? (song as Asset)?.id
                            : (song as TributeVideoAsset).asset?.id,
                          index
                        )
                      }
                      className={hasEvent ? 'ml-auto' : 'ml-4'}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </div>
              </li>
            );
          })}
          {isUploading && uploadingSong && isTab && (
            <div className='flex h-6 items-center'>
              <MusicIcon fill='var(--color-primary-black)' className='h-4 w-6' />
              <div className='ml-3 max-w-[80%] cursor-pointer truncate whitespace-nowrap text-sm font-normal'>
                {uploadingSong.title}
              </div>
              <div className='ml-auto mr-2 size-4 overflow-hidden'>
                <Spinner />
              </div>
            </div>
          )}
        </ul>
      )}
      {showEditMusicPopup && (
        <EditSongPopup
          songUrl={editingSongUrl}
          originalAssetName={editingAssetName}
          setShowEditMusicPopup={setShowEditMusicPopup}
          setEditingSongUrl={setEditingSongUrl}
          setEditingAssetName={setEditingAssetName}
          onSongSaved={handleSongSaved}
          eventId={eventId}
        />
      )}
    </div>
  );
};
